"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useBodyScrollLock from "@core/helpers/client/hooks/useBodyScrollLock";

// مدت انیمیشن بسته شدن؛ بعد از این مدت شیت از DOM حذف می‌شود
const CLOSE_DURATION = 400;

export default function BottomSheet({
    open,
    onClose,
    children,
    full = false,
    partialHeight = "45%",
    allowBackdropClose = true,
    allowDragToClose = true,
    // برای شیت‌هایی که محتوایشان باید همیشه در DOM باشد (مثل اینپوت جستجو که
    // باید در همان کلیک کاربر فوکوس بگیرد تا کیبورد موبایل باز شود)
    keepMounted = false,
}) {

    const sheetRef = useRef(null);

    const startY = useRef(0);
    const currentY = useRef(0);
    const startTime = useRef(0);
    const dragging = useRef(false);
    const closeTimeout = useRef(null);

    // dvh نه vh: در موبایل 100vh ارتفاعِ بدونِ نوار آدرس مرورگر است و شیت
    // تمام‌صفحه بلندتر از ناحیه‌ی قابل‌دیدن می‌شد و فوتر زیر نوار مرورگر می‌رفت
    const height = full ? "100dvh" : partialHeight;

    // BUG FIX: قبلا شیت همیشه در DOM بود و بدون هیچ ترنسفورم اولیه رندر می‌شد؛
    // بعد افکت اجرا می‌شد و آن را با انیمیشن به پایین می‌برد. نتیجه این بود که
    // در هر صفحه (چون سرچ‌بار هدر یک BottomSheet دارد) شیت یک لحظه روی صفحه
    // می‌آمد و بعد پایین می‌رفت.
    //
    // الان: `is_mounted` تعیین می‌کند شیت در DOM باشد یا نه و `is_shown`
    // موقعیت باز/بسته را کنترل می‌کند. شیت بسته اصلا رندر نمی‌شود.
    const [is_mounted, set_is_mounted] = useState(false);
    const [is_shown, set_is_shown] = useState(false);

    // شیت با پورتال مستقیم زیر body می‌رود. دلیلش این است که والدهای شیت
    // (مثل نوار جستجوی چسبانِ صفحه‌ی سرچ با `sticky z-30`) خودشان یک
    // stacking context می‌سازند و z-index شیت داخل همان محدوده حبس می‌شد؛
    // نتیجه اینکه نویگیشن پایین صفحه (z-30 ولی پایین‌تر در DOM) روی شیت
    // می‌افتاد. با پورتال، شیت در ریشه‌ی صفحه و بالای همه قرار می‌گیرد.
    const [can_portal, set_can_portal] = useState(false);
    useEffect(() => set_can_portal(true), []);

    useEffect(() => {
        if (open) {
            clearTimeout(closeTimeout.current);
            set_is_mounted(true);
            return;
        }

        set_is_shown(false);

        closeTimeout.current = setTimeout(() => set_is_mounted(false), CLOSE_DURATION);

        return () => clearTimeout(closeTimeout.current);
    }, [open]);

    // یک فریم بعد از قرار گرفتن در DOM باز می‌شود تا انیمیشن ورود دیده شود
    useEffect(() => {
        if (!is_mounted || !open) return;

        const frame = requestAnimationFrame(() => set_is_shown(true));

        return () => cancelAnimationFrame(frame);
    }, [is_mounted, open]);

    // اسکرول صفحه فقط تا وقتی شیت باز است قفل می‌شود (قفل شمارشی core)
    useBodyScrollLock(open);

    // پاک کردن استایل‌های inline که هنگام درگ ست شده‌اند تا کلاس‌ها دوباره
    // کنترل موقعیت را در دست بگیرند
    const clear_inline_transform = useCallback(() => {
        const sheet = sheetRef.current;
        if (!sheet) return;

        sheet.style.transition = "";
        requestAnimationFrame(() => {
            if (sheetRef.current) sheetRef.current.style.transform = "";
        });
    }, []);

    const onDrag = useCallback((e) => {
        if (!dragging.current || !sheetRef.current) return;

        const y = e.touches?.[0]?.clientY || e.clientY;
        const delta = y - startY.current;

        if (delta < 0) return;

        currentY.current = delta;
        sheetRef.current.style.transform = `translateY(${delta}px)`;
    }, []);

    const endDrag = useCallback(() => {
        if (!dragging.current) return;

        dragging.current = false;

        window.removeEventListener("mousemove", onDrag);
        window.removeEventListener("mouseup", endDrag);
        window.removeEventListener("touchmove", onDrag);
        window.removeEventListener("touchend", endDrag);

        const delta = currentY.current;
        const time = Math.max(Date.now() - startTime.current, 1);
        const velocity = delta / time;

        currentY.current = 0;

        const sheet = sheetRef.current;
        if (!sheet) return;

        if (delta > 150 || velocity > 0.65) {
            sheet.style.transition = "transform .35s cubic-bezier(.22,1,.36,1)";
            sheet.style.transform = "translateY(100%)";

            clearTimeout(closeTimeout.current);
            closeTimeout.current = setTimeout(() => {
                clear_inline_transform();
                onClose?.();
            }, 350);

            return;
        }

        // برگشت به موقعیت باز با همان انیمیشن کلاس‌ها
        clear_inline_transform();
    }, [clear_inline_transform, onClose, onDrag]);

    const startDrag = (e) => {
        if (!allowDragToClose || full || !sheetRef.current) return;

        dragging.current = true;
        startY.current = e.touches?.[0]?.clientY || e.clientY;
        startTime.current = Date.now();

        sheetRef.current.style.transition = "none";

        window.addEventListener("mousemove", onDrag);
        window.addEventListener("mouseup", endDrag);
        window.addEventListener("touchmove", onDrag);
        window.addEventListener("touchend", endDrag);
    };

    // نشتی لیسنر/تایمر هنگام آن‌مانت
    useEffect(() => () => {
        clearTimeout(closeTimeout.current);
        window.removeEventListener("mousemove", onDrag);
        window.removeEventListener("mouseup", endDrag);
        window.removeEventListener("touchmove", onDrag);
        window.removeEventListener("touchend", endDrag);
    }, [onDrag, endDrag]);

    if (!is_mounted && !keepMounted) return null;

    // در سرور و اولین رندر کلاینت چیزی رندر نمی‌شود تا hydration به هم نریزد
    if (!can_portal) return null;

    const stopClick = (e) => e.stopPropagation();

    return createPortal(
        <>
            {/* backdrop */}
            <div
                className={`fixed inset-0 bg-black/30 transition-opacity duration-300 z-40
        ${is_shown ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                onClick={() => allowBackdropClose && onClose?.()}
            />

            {/* sheet */}
            <div
                ref={sheetRef}
                className={`fixed left-0 bottom-0 z-40 bg-jmt-white shadow-xl will-change-transform
        transition-transform duration-400 ease-out
        ${is_shown ? "translate-y-0" : "translate-y-full"}
        ${full ? "top-0 rounded-none" : "rounded-t-lg"}`}
                style={{
                    height,
                    width: "100%",
                }}
                onClick={stopClick}
            >

                {/* handle */}
                {!full && allowDragToClose && (
                    <div
                        className="w-full flex justify-center cursor-grab active:cursor-grabbing"
                        onMouseDown={startDrag}
                        onTouchStart={startDrag}>

                        <div className="w-9 h-[3px] bg-jmt-neutral-200 rounded-full mt-2"></div>
                    </div>
                )}

                <div className={`overflow-y-auto ${!full && allowDragToClose ? "h-[calc(100%-16px)]!" : "h-full"}`}>
                    {children}
                </div>

            </div>
        </>,
        document.body
    );
}
